import { useEffect, useRef } from "react";

import type { RowData } from "@tanstack/react-table-v9";
import queryString from "query-string";

import type { TableV2Instance } from "./useTableV2";

/** Page, sort and search in the shape DRF list endpoints expect */
export interface TableV2ServerQuery {
  page: number;
  page_size: number;
  ordering?: string;
  search?: string;
}

export interface UseTableV2ServerQueryOptions {
  /** Filter query string, e.g. from AdvancedFilters. A change goes back to page 1 */
  filters?: string;
  /** Called with the full query string whenever it changes */
  fetch?: (query: string) => void;
  /** Holds off fetching, e.g. until AdvancedFilters has loaded */
  enabled?: boolean;
}

/** Builds a server-mode table's request from its filters, page, sort and search, and refetches when any of them change */
export const useTableV2ServerQuery = <TData extends RowData>(
  table: TableV2Instance<TData>,
  { filters, fetch, enabled = true }: UseTableV2ServerQueryOptions = {},
) => {
  const { pagination, sorting, globalFilter } = table.state;

  // Filters changed this render: ask for page 1 now instead of fetching the old page first
  const previousFilters = useRef(filters);
  const filtersChanged = enabled && previousFilters.current !== filters;
  useEffect(() => {
    if (previousFilters.current === filters) return;
    previousFilters.current = filters;
    if (enabled && pagination.pageIndex !== 0) table.setPageIndex(0);
  }, [filters]);

  const ordering = sorting
    .map(({ id, desc }) => {
      const field = table.getColumn(id)?.columnDef.meta?.sortKey ?? id;
      return `${desc ? "-" : ""}${field}`;
    })
    .join(",");
  const search = typeof globalFilter === "string" ? globalFilter.trim() : "";

  const tableQuery: TableV2ServerQuery = {
    page: filtersChanged ? 1 : pagination.pageIndex + 1,
    page_size: pagination.pageSize,
    ordering: ordering || undefined,
    search: search || undefined,
  };
  const query = [filters?.replace(/^\?/, ""), queryString.stringify(tableQuery)]
    .filter(Boolean)
    .join("&");

  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  });
  useEffect(() => {
    if (enabled) fetchRef.current?.(query);
  }, [query, enabled]);

  return { query, tableQuery };
};
